(() => {
  // shared/look-canvas.ts
  var paintTokens = /* @__PURE__ */ new WeakMap();
  var geometryCache = /* @__PURE__ */ new Map();
  var defaultFit = { crop: 73.7, scale: 120, offset: -175 };
  function cleanFit(fit = {}) {
    const number = (key, min, max) => Number.isFinite(fit[key]) ? Math.max(min, Math.min(max, fit[key])) : defaultFit[key];
    return { crop: number("crop", 50, 90), scale: number("scale", 70, 160), offset: number("offset", -500, 300) };
  }
  async function inspectImage(product) {
    const url = product.image;
    if (!geometryCache.has(url)) geometryCache.set(url, new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const w = 300, h = Math.round(w * img.naturalHeight / img.naturalWidth), canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        try {
          if (!ctx) throw new Error("Canvas unavailable");
          ctx.drawImage(img, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h).data;
          const bounds = (x0, x1, y0 = 0, y1 = h) => {
            let minX = w, minY = h, maxX = 0, maxY = 0;
            for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) if (data[(y * w + x) * 4 + 3] > 24) {
              minX = Math.min(x, minX);
              maxX = Math.max(x + 1, maxX);
              minY = Math.min(y, minY);
              maxY = Math.max(y + 1, maxY);
            }
            return maxX > minX ? [minX, minY, maxX, maxY] : [x0, y0, x1, y1];
          };
          const b = bounds(0, w), mid = Math.round((b[0] + b[2]) / 2), legY = Math.round(b[1] + (b[3] - b[1]) * 0.85);
          const legs = [bounds(0, mid, legY, b[3]), bounds(mid, w, legY, b[3])].map((a) => (a[0] + a[2]) / 2);
          resolve({ width: w, height: h, bounds: b, leg_centers: legs, sock_halves: [bounds(0, mid), bounds(mid, w)], split_x: mid });
        } catch {
          resolve({ width: w, height: h, bounds: [0, 0, w, h], leg_centers: [w * 0.39, w * 0.61], sock_halves: [[w * 0.25, 0, w * 0.49, h], [w * 0.51, 0, w * 0.75, h]], split_x: w / 2 });
        }
      };
      img.onerror = () => reject(new Error("A look image could not load."));
      img.src = url;
    }));
    return geometryCache.get(url);
  }
  async function paintLook(element, products, fit = {}) {
    const token = /* @__PURE__ */ Symbol();
    paintTokens.set(element, token);
    const g = await Promise.all(products.map(inspectImage));
    if (paintTokens.get(element) !== token) return;
    const f = cleanFit(fit), [top, bib, sock] = g;
    const apparelScale = 0.8;
    const join = apparelScale * (-275 + 1200 * top.height / top.width * f.crop / 100);
    const bibBottom = apparelScale * (f.offset + bib.bounds[3] * (1200 * f.scale / 100 / bib.width)), sockScale = 280 / (sock.bounds[2] - sock.bounds[0]);
    const legs = bib.leg_centers.map((x) => 600 + (x - bib.width / 2) * (1200 * f.scale / 100 / bib.width) * apparelScale);
    const halves = sock.sock_halves.map((b, i) => ({ x: legs[i] - ((b[0] + b[2]) / 2 - sock.width / 2) * sockScale, y: bibBottom + 50 - b[1] * sockScale, end: bibBottom + 50 + (b[3] - b[1]) * sockScale + 45 }));
    const height = Math.max(1600, ...halves.map((h) => h.end));
    element.replaceChildren();
    Object.assign(element.style, { position: "relative", isolation: "isolate", width: "100%", aspectRatio: `1200/${height}`, overflow: "hidden" });
    function picture(product, css) {
      const image = document.createElement("img");
      image.src = product.image;
      image.alt = "";
      image.draggable = false;
      Object.assign(image.style, { position: "absolute", display: "block", maxWidth: "none", height: "auto", left: "50%", transform: "translateX(-50%)", pointerEvents: "none", ...css });
      return image;
    }
    const layer = document.createElement("div");
    Object.assign(layer.style, { position: "absolute", inset: "0", clipPath: `inset(${(join - 2.5) / height * 100}% 0 0 0)` });
    layer.append(picture(products[1], { width: f.scale * apparelScale + "%", top: f.offset * apparelScale / height * 100 + "%" }));
    element.append(layer);
    element.append(picture(products[0], { width: apparelScale * 100 + "%", top: -275 * apparelScale / height * 100 + "%", zIndex: "2", clipPath: `inset(0 0 ${100 - f.crop}% 0)` }));
    halves.forEach((h, i) => element.append(picture(products[2], { width: sock.width * sockScale / 1200 * 100 + "%", left: h.x / 1200 * 100 + "%", top: h.y / height * 100 + "%", zIndex: "3", clipPath: i ? `inset(0 0 0 ${sock.split_x / sock.width * 100}%)` : `inset(0 ${100 - sock.split_x / sock.width * 100}% 0 0)` })));
  }

  // shared/offer-estimate.js
  function kitOfferEstimate(offer, mode, products, profile) {
    const zero = { saving: 0, eligible: false };
    if (mode !== "live" || !offer || products.some((p) => !p)) return zero;
    const money = (p) => Number.isFinite(p.price) && p.price >= 0 ? p.price : 0;
    if (offer.kind === "bogo") {
      const match = (p, choices) => choices.some((c) => c.id && String(c.id).split("/").pop() === String(p.id).split("/").pop());
      const buy = products.map((p, i) => match(p, offer.buyProducts) ? i : -1).filter((i) => i >= 0);
      const reward = products.map((p, i) => match(p, offer.rewardProducts) ? i : -1).filter((i) => i >= 0);
      if (!Number.isInteger(offer.buyQuantity) || offer.buyQuantity < 1 || !Number.isInteger(offer.rewardQuantity) || offer.rewardQuantity < 1) return zero;
      const sets = Math.min(Math.floor(buy.length / offer.buyQuantity), Math.floor(reward.length / offer.rewardQuantity), Math.floor((/* @__PURE__ */ new Set([...buy, ...reward])).size / (offer.buyQuantity + offer.rewardQuantity)));
      if (!sets) return zero;
      buy.sort((a, b) => Number(reward.includes(a)) - Number(reward.includes(b)) || money(products[b]) - money(products[a]));
      const paid = new Set(buy.slice(0, sets * offer.buyQuantity));
      const free = reward.filter((i) => !paid.has(i)).sort((a, b) => money(products[a]) - money(products[b])).slice(0, sets * offer.rewardQuantity);
      return { saving: free.reduce((sum, i) => sum + money(products[i]), 0), eligible: free.length > 0 };
    }
    if (!["percentage", "free_item"].includes(offer.kind) || !["all", profile].includes(offer.profile)) return zero;
    const required = [offer.top, offer.bottom, offer.socks];
    const rewards = [0, 0, 0];
    if (offer.kind === "free_item") {
      const i = ["top", "bottom", "socks"].indexOf(offer.rewardCategory);
      if (i < 0) return zero;
      rewards[i] = offer.rewardQuantity;
    }
    const needed = required.map((n, i) => n + rewards[i]);
    if (needed.some((n) => !Number.isInteger(n) || n < 0 || n > 1) || !needed.some((n) => n > 0)) return zero;
    for (let i = 0; i < 2; i++) if (needed[i] && offer.range !== "all" && !products[i].tags.includes("level:" + offer.range.toLowerCase())) return zero;
    if (offer.kind === "percentage") {
      if (!Number.isFinite(offer.percentage) || offer.percentage <= 0 || offer.percentage > 100) return zero;
      return { saving: products.reduce((sum, p, i) => sum + (required[i] ? money(p) * offer.percentage / 100 : 0), 0), eligible: true };
    }
    return { saving: products.reduce((sum, p, i) => sum + (rewards[i] ? money(p) : 0), 0), eligible: true };
  }

  // shared/storefront-looks.js
  var escapeHTML = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  var productCache = /* @__PURE__ */ new Map();
  function eligible(p, profile, category, range) {
    if (!p?.image || !p.variants?.some((v) => v.available) || p.tags.some((t) => ["archive-sale", "#archive-sale"].includes(t.toLowerCase().trim()))) return false;
    const has = (t) => p.tags.includes(t), cycling = profile.endsWith("cycling");
    const cyclingTee = (has("type:tee") || has("type:tee-ls")) && has("sport:cycling") && !has("sport:running");
    const ct = has("category:jersey") || cyclingTee, cb = has("category:bib"), rt = !cyclingTee && ["tee", "singlet", "crop"].some((t) => has("type:" + t)), rb = ["short", "tight"].some((t) => has("type:" + t));
    if (category === 2) return has("type:sock") && !ct && !cb && !rt && !rb && (has("sport:cycling") || has("sport:running"));
    if (has("type:sock") || has("gender:male") === has("gender:female") || !has("gender:" + (profile.startsWith("mens") ? "male" : "female")) || !has("sport:" + (cycling ? "cycling" : "running")) || !has("level:" + range.toLowerCase()) || range === "360" && has("level:pinnacle")) return false;
    return cycling ? !rt && !rb && (category === 0 ? ct && !cb : cb && !ct) : !ct && !cb && (category === 0 ? rt && !rb : rb && !rt);
  }
  function matchingLooks(management, config) {
    const ids = config.ids.split(",").map((s) => s.trim()).filter(Boolean);
    return Object.entries(management?.profiles || {}).flatMap(([profile, p]) => (p.looks || []).filter((l) => l.shopTheLook === true && l.products.length === 3 && (config.profile === "all" || config.profile === profile) && (config.range === "All" || config.range === l.range) && (!ids.length || ids.includes(l.id)) && (config.layout !== "rail" || l.productPages !== false && l.products.slice(0, 2).includes(config.product))).map((l) => ({ ...l, profile })));
  }
  var ShopLooks = class extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.selections = /* @__PURE__ */ new Map();
      this.busy = false;
      this.config = JSON.parse(this.querySelector("[data-look-config]").textContent);
      this.labels = this.config.labels;
      this.content = this.querySelector("[data-look-content]");
      this.message = this.querySelector(".look-load-message");
      if (this.config.b2b) {
        this.hidden = true;
        return;
      }
      if (this.config.layout !== "rail") {
        const requested = new URL(location.href).searchParams.get("look-profile");
        this.config.profile = ["mens-cycling", "womens-cycling", "mens-running", "womens-running"].includes(requested) ? requested : this.config.profile === "all" ? "mens-cycling" : this.config.profile;
      }
      await this.loadLooks();
    }
    async loadLooks() {
      const generation = /* @__PURE__ */ Symbol();
      this.generation = generation;
      this.message.textContent = "";
      const candidates = matchingLooks(this.config.management, this.config);
      this.looks = candidates.map((l) => ({ ...l, items: null }));
      if (this.config.layout === "rail") {
        try {
          for (const l of this.looks) {
            const items = await Promise.all(l.products.map((h) => this.product(h)));
            if (items.every((p, i) => eligible(p, l.profile, i, l.range))) {
              this.looks = [{ ...l, items }];
              this.render();
              return;
            }
          }
          this.hidden = true;
        } catch {
          this.hidden = true;
        }
        return;
      }
      this.render();
      let failed = false;
      await Promise.all(this.looks.map(async (l, i) => {
        try {
          const items = await Promise.all(l.products.map((h) => this.product(h)));
          if (this.generation !== generation) return;
          const slot = this.querySelector(`[data-look-slot="${i}"]`);
          if (!items.every((p, j) => eligible(p, l.profile, j, l.range))) {
            slot?.remove();
            return;
          }
          l.items = items;
          slot.innerHTML = this.card(l, i);
          slot.removeAttribute("aria-busy");
          this.bindCard(slot, l, i);
        } catch {
          if (this.generation === generation) {
            failed = true;
            this.querySelector(`[data-look-slot="${i}"]`)?.remove();
          }
        }
      }));
      if (this.generation !== generation) return;
      this.querySelectorAll(".look-range").forEach((el) => {
        if (!el.querySelector("article")) el.remove();
      });
      if (!this.looks.some((l) => l.items)) this.querySelector(".look-results").innerHTML = `<p class="look-empty">${escapeHTML(failed ? this.labels.load_error : this.labels.empty)}</p>`;
      else if (failed) this.message.textContent = this.labels.load_error;
    }
    skeleton() {
      return `<div class="look-skeleton" aria-label="${escapeHTML(this.labels.loading)}"><span class="skeleton-top"></span><span class="skeleton-bib"></span><span class="skeleton-socks"></span></div>`;
    }
    profileHeading() {
      return (this.config.profile.startsWith("mens") ? this.labels.mens : this.labels.womens) + " " + (this.config.profile.endsWith("cycling") ? this.labels.cycling : this.labels.running);
    }
    sidebar() {
      const [gender, sport] = this.config.profile.split("-");
      const nav = (value, label, type) => `<button type="button" data-profile="${type === "gender" ? value + "-" + sport : gender + "-" + value}" aria-pressed="${(type === "gender" ? gender : sport) === value}">${escapeHTML(label)}</button>`;
      return `<aside class="look-sidebar"><div class="look-sidebar-inner"><p class="look-eyebrow">${escapeHTML(this.profileHeading())}</p><nav class="look-profile-nav" aria-label="${escapeHTML(this.labels.browse)}"><div role="group" aria-label="${escapeHTML(this.labels.gender)}">${nav("mens", this.labels.men, "gender")}${nav("womens", this.labels.women, "gender")}</div><div role="group" aria-label="${escapeHTML(this.labels.sport)}">${nav("cycling", this.labels.cycling, "sport")}${nav("running", this.labels.running, "sport")}</div></nav><h1>${escapeHTML(this.labels.heading).replace(" THE ", "<br>THE ")}</h1><p class="look-offer">${escapeHTML(this.offerCopy())}</p></div></aside>`;
    }
    card(l, i) {
      return `<button type="button" class="look-image-button" data-open="${i}" aria-label="${escapeHTML(this.labels.view + " " + l.name)}"><div class="look-composition" data-canvas="${i}" role="img" aria-label="${escapeHTML(l.name)}">${this.skeleton()}</div>${l.items[0].hoverImage ? `<img class="look-lifestyle" src="${escapeHTML(l.items[0].hoverImage)}" alt="" loading="lazy" decoding="async">` : ""}</button><div class="look-card-info"><h3>${escapeHTML(l.name)}</h3><p>${escapeHTML(l.items.map((_, j) => this.label(j, l)).join(" + "))}</p><p>${this.money(l.items.reduce((sum, p) => sum + p.variants[0].price, 0))}</p><button type="button" data-open="${i}">${escapeHTML(this.labels.view)}</button></div>`;
    }
    bindCard(slot, l, i) {
      slot.querySelectorAll("[data-open]").forEach((b) => b.addEventListener("click", () => this.open(i, b)));
      const lifestyle = slot.querySelector(".look-lifestyle");
      if (lifestyle) {
        const ready = () => {
          if (lifestyle.naturalWidth) lifestyle.classList.add("is-ready");
        };
        lifestyle.addEventListener("load", ready);
        ready();
      }
      const el = slot.querySelector("[data-canvas]");
      paintLook(el, l.items, l.fit).catch(() => {
        el.textContent = this.labels.image_error;
      });
    }
    async product(handle, fresh = false) {
      const url = this.config.root + "products/" + encodeURIComponent(handle) + "?view=bs-look-data";
      if (fresh || !productCache.has(url)) productCache.set(url, fetch(url, { credentials: "same-origin", cache: "no-store" }).then(async (r) => {
        if (r.status === 404) return null;
        if (!r.ok) throw new Error();
        return r.json();
      }).catch((e) => {
        productCache.delete(url);
        throw e;
      }));
      return productCache.get(url);
    }
    money(n) {
      return new Intl.NumberFormat(document.documentElement.lang || "en", { style: "currency", currency: this.config.currency }).format(n / 100);
    }
    label(i, look) {
      return this.labels[i === 2 ? "socks" : i === 0 ? look.profile.endsWith("cycling") ? "jersey" : "top" : look.profile.endsWith("cycling") ? "bib" : "bottom"];
    }
    offerCopy() {
      const { offer, mode } = this.config.management || {};
      return mode === "live" && offer?.kind === "percentage" && offer.profile === "all" && offer.range === "all" && offer.top === 1 && offer.bottom === 1 && offer.socks === 1 ? this.labels.offer.replace("{percent}", String(offer.percentage)) : "";
    }
    render() {
      if (this.config.layout === "rail") {
        const l = this.looks[0];
        this.content.innerHTML = `<div class="look-rail"><h2>${escapeHTML(this.offerCopy() ? this.labels.wear.replace("{percent}", String(this.config.management.offer.percentage)) : this.labels.shop)}</h2><button type="button" class="look-rail-row" data-open="0" aria-label="${escapeHTML(this.labels.shop + " \u2014 " + l.name)}"><span class="look-rail-items">${l.items.map((p, i) => `<span><img src="${escapeHTML(p.image)}" alt="" width="44" height="58"><small>${escapeHTML(this.label(i, l))}</small></span>`).join('<i class="look-rail-plus" aria-hidden="true">+</i>')}</span><span class="look-rail-cta">${escapeHTML(this.labels.shop)}</span></button></div>`;
      } else {
        this.content.innerHTML = `<div class="look-gallery">${this.sidebar()}<div class="look-results">${this.looks.length ? ["Pinnacle", "360"].map((range) => {
          const looks = this.looks.filter((l) => l.range === range);
          if (!looks.length) return "";
          return `<section class="look-range"><h2>${range}</h2><div class="look-cards">${looks.map((l) => `<article data-look-slot="${this.looks.indexOf(l)}" aria-busy="true">${this.skeleton()}<div class="look-card-info"><h3>${escapeHTML(l.name)}</h3><p>${escapeHTML(this.labels.loading)}</p></div></article>`).join("")}</div></section>`;
        }).join("") : `<p class="look-empty">${escapeHTML(this.labels.empty)}</p>`}</div></div>`;
        this.querySelectorAll("[data-profile]").forEach((b) => b.addEventListener("click", () => {
          if (this.config.profile === b.dataset.profile) return;
          this.config.profile = b.dataset.profile;
          const url = new URL(location.href);
          url.searchParams.set("look-profile", this.config.profile);
          history.replaceState(null, "", url);
          this.loadLooks();
        }));
      }
      this.querySelectorAll("[data-open]").forEach((b) => b.addEventListener("click", () => this.open(Number(b.dataset.open), b)));
    }
    chosen() {
      const values = this.selections.get(this.active.id) || [];
      return this.active.items.map((p, i) => p.variants.find((v) => v.id === values[i] && v.available));
    }
    update() {
      const chosen = this.chosen(), count = chosen.filter(Boolean).length;
      this.dialog.querySelector("[data-progress]").textContent = this.labels.progress.replace("{count}", count);
      const add = this.dialog.querySelector("[data-add]");
      add.disabled = this.busy || count !== 3;
      add.textContent = this.busy ? this.labels.adding : this.added ? this.labels.view_bag : count === 3 ? this.labels.add : this.labels.choose.replace("{count}", count);
      const total = this.dialog.querySelector("[data-total]");
      total.hidden = count !== 3;
      if (count === 3) {
        const original = chosen.reduce((s, v) => s + v.price, 0);
        const offer = kitOfferEstimate(this.config.management.offer, this.config.management.mode, this.active.items.map((p, i) => ({ ...p, price: chosen[i].price })), this.active.profile);
        total.innerHTML = `${offer.saving ? `<s>${this.money(original)}</s>` : ""}<strong>${this.money(original - Math.round(offer.saving))}</strong><small>${escapeHTML(this.config.currency)}</small>`;
      }
      this.dialog.querySelectorAll("[data-item-price]").forEach((el, i) => {
        el.textContent = this.money((chosen[i] || this.active.items[i].variants[0]).price);
      });
      this.dialog.querySelectorAll("[data-size-group]").forEach((group, i) => group.querySelectorAll("[data-variant]").forEach((b) => {
        b.setAttribute("aria-pressed", String(Number(b.dataset.variant) === chosen[i]?.id));
        b.disabled = this.busy || !this.active.items[i].variants.find((v) => v.id === Number(b.dataset.variant))?.available;
      }));
    }
    open(index, opener) {
      if (this.busy) return;
      this.active = this.looks[index];
      this.opener = opener;
      this.added = false;
      const l = this.active;
      if (!this.selections.has(l.id)) this.selections.set(l.id, []);
      this.dialog?.remove();
      const dialog = document.createElement("dialog");
      this.dialog = dialog;
      dialog.className = "look-dialog";
      dialog.setAttribute("aria-label", l.name);
      dialog.innerHTML = `<div class="look-dialog-head"><button type="button" data-close>${escapeHTML(this.labels.close)}</button></div><div class="look-dialog-grid"><div class="look-composition" role="img" aria-label="${escapeHTML(l.name)}"></div><div><h2>${escapeHTML(l.name)}</h2>${l.items.map((p, i) => `<fieldset data-size-group="${i}"><legend>${escapeHTML(this.label(i, l))}</legend><div class="look-product-line"><a href="${escapeHTML(this.config.root + "products/" + p.handle)}">${escapeHTML(p.title)}</a><span data-item-price>${this.money(p.variants[0].price)}</span></div><div class="look-sizes">${p.variants.map((v) => `<button type="button" data-variant="${v.id}" aria-label="${escapeHTML(this.label(i, l) + " " + v.size + (v.available ? "" : " \u2014 " + this.labels.sold_out))}" aria-pressed="false" ${v.available ? "" : "disabled"}>${escapeHTML(v.size)}</button>`).join("")}</div></fieldset>`).join("")}<div class="look-purchase"><div class="look-summary"><p data-progress role="status"></p><p data-total hidden></p></div><button type="button" data-add></button><p data-cart-error role="alert"></p><a class="look-build" href="${escapeHTML(this.config.root + "pages/" + l.profile + "-kit")}">${escapeHTML(this.labels.build)}</a></div></div></div>`;
      this.append(dialog);
      paintLook(dialog.querySelector(".look-composition"), l.items, l.fit).catch(() => {
      });
      dialog.querySelector("[data-close]").onclick = () => dialog.close();
      dialog.addEventListener("close", () => opener.focus());
      dialog.querySelectorAll("[data-size-group]").forEach((group, i) => group.querySelectorAll("[data-variant]").forEach((b) => b.onclick = () => {
        if (this.busy) return;
        this.selections.get(l.id)[i] = Number(b.dataset.variant);
        this.added = false;
        this.update();
      }));
      dialog.querySelector("[data-add]").onclick = () => this.add();
      this.update();
      dialog.showModal();
    }
    async drawer() {
      const drawer = document.querySelector("cart-drawer"), cartItems = drawer?.querySelector("cart-items");
      if (!drawer || typeof cartItems?.build !== "function") {
        location.href = this.config.root + "cart";
        return;
      }
      const response = await fetch(this.config.root + "cart?section_id=api-cart-items", { credentials: "same-origin", cache: "no-store" });
      if (!response.ok) throw new Error();
      const parsed = new DOMParser().parseFromString(await response.text(), "text/html"), contents = parsed.querySelector("[data-api-content]");
      if (!contents) throw new Error();
      cartItems.build(contents);
      this.dialog.close();
      if (window.accessibility) window.accessibility.lastElement = this.opener;
      drawer.dispatchEvent(new CustomEvent("theme:cart-drawer:show"));
    }
    async add() {
      if (this.busy) return;
      if (this.added) {
        location.href = this.config.root + "cart";
        return;
      }
      const variants = this.chosen();
      if (variants.some((v) => !v)) return;
      this.busy = true;
      this.update();
      const error = this.dialog.querySelector("[data-cart-error]");
      error.textContent = "";
      try {
        const response = await fetch(this.config.root + "cart/add.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: variants.map((v) => ({ id: v.id, quantity: 1 })) }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.labels.stock_error);
        this.added = true;
        await this.drawer();
      } catch (e) {
        error.textContent = this.added ? this.labels.drawer_error : e.message || this.labels.stock_error;
      } finally {
        this.busy = false;
        this.update();
      }
    }
  };
  if (!customElements.get("bs-shop-looks")) customElements.define("bs-shop-looks", ShopLooks);
})();
